import React, { useState, useMemo, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import AppBar from '../components/AppBar'
import SubmitButton from '../components/SubmitButton'
import CustomCalendar from '../components/CustomCalendar'
import FormErrorMessage from '../components/FormErrorMessage'
import type { SelectOption } from '../components/CustomSelect'
import {
  buildWaitingTimeOptions,
  toWaitingTimeOption,
  WAITING_TIME_NONE,
} from '../constants/waitingTime'
import LoadingSpinner from '../components/LoadingSpinner'
import { toastUtil } from '../utils/toast'
import { useReviewStore } from '../stores/reviewStore'
import { mapApi } from '../api/map'
import Divider from '../components/Divider'
import SheetHeader from '../components/SheetHeader'
import { useSheetDragDismiss } from '../hooks/useSheetDragDismiss'

const VISIT_TIME_PART_MAX_LEN = 2
/** 저장된 값을 읽을 때 쓰는 형식 — 10분 단위 도입 전 데이터(13:37 등)도 받아야 한다 */
const VISIT_TIME_REGEX = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/
/** 새로 입력받을 때 쓰는 형식 — 분은 10분 단위만 허용 */
const VISIT_TIME_INPUT_REGEX = /^([0-1]?[0-9]|2[0-3]):[0-5]0$/
const VISIT_TIME_FORMAT_MESSAGE =
  '시간은 10분 단위로 입력해주세요 (예: 13:30)'
const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/

/** 방문 시간 피커의 분 단위 — 00, 10, 20, 30, 40, 50 */
const VISIT_MINUTE_STEP = 10

/**
 * 피커에 없는 분(직접 입력했거나 예전에 저장된 37 같은 값)을 선택 가능한 값으로 맞춘다.
 * 맞추지 않으면 목록에서 아무것도 선택 표시가 안 된다. 시(hour)를 넘기지 않도록 내림한다.
 */
const snapMinuteToStep = (minute: string): string => {
  const parsed = Number.parseInt(minute, 10)
  if (Number.isNaN(parsed)) return '00'
  const clamped = Math.min(Math.max(parsed, 0), 59)
  const snapped = Math.floor(clamped / VISIT_MINUTE_STEP) * VISIT_MINUTE_STEP
  return String(snapped).padStart(2, '0')
}

const parseVisitTimeParts = (
  timePart: string | undefined
): { hour: string; minute: string } => {
  if (!timePart) {
    return { hour: '', minute: '' }
  }
  const timeParts = timePart.split(':')
  if (timeParts.length < 2) {
    return { hour: '', minute: '' }
  }
  const hour = timeParts[0].replace(/\D/g, '').slice(0, VISIT_TIME_PART_MAX_LEN)
  const minute = timeParts[1]
    .replace(/\D/g, '')
    .slice(0, VISIT_TIME_PART_MAX_LEN)

  return { hour, minute }
}

/** 스토어 visitDate: YYYY-MM-DD | HH:mm | YYYY-MM-DD HH:mm */
const parseFromStoreVisitDate = (
  storeVisitDate: string | null
): { selectedDate: Date | null; hour: string; minute: string } => {
  if (!storeVisitDate?.trim()) {
    return { selectedDate: null, hour: '', minute: '' }
  }

  const t = storeVisitDate.trim()

  if (DATE_ONLY_REGEX.test(t)) {
    return { selectedDate: new Date(t), hour: '', minute: '' }
  }

  if (VISIT_TIME_REGEX.test(t)) {
    const { hour, minute } = parseVisitTimeParts(t)

    return { selectedDate: null, hour, minute }
  }

  const [datePart, ...restParts] = t.split(/\s+/)
  const timePart = restParts.join(' ')

  if (datePart && DATE_ONLY_REGEX.test(datePart)) {
    const { hour, minute } = parseVisitTimeParts(timePart)

    return { selectedDate: new Date(datePart), hour, minute }
  }

  return { selectedDate: null, hour: '', minute: '' }
}

/**
 * 저장된 방문 일시가 없으면 리뷰를 쓰는 지금 시각을 기본값으로 쓴다.
 * 분은 피커 단위에 맞춰 내림한다 (10:23 → 10:20).
 * 이미 값이 있으면(리뷰 수정 등) 그대로 두고, 비어 있는 항목만 채운다.
 */
const resolveInitialVisitFields = (
  storeVisitDate: string | null
): { selectedDate: Date | null; hour: string; minute: string } => {
  const parsed = parseFromStoreVisitDate(storeVisitDate)
  const now = new Date()

  return {
    selectedDate: parsed.selectedDate ?? now,
    hour: parsed.hour || String(now.getHours()).padStart(2, '0'),
    // 10분 단위 도입 전 저장된 값(13:37 등)도 폼에서는 단위에 맞춰 보여준다.
    // 그대로 두면 사용자가 건드리지도 않은 값 때문에 저장 시 형식 오류가 난다.
    minute: snapMinuteToStep(parsed.minute || String(now.getMinutes())),
  }
}

const VisitDateAdd: React.FC = () => {
  const navigate = useNavigate()
  const {
    visitDate: storeVisitDate,
    setVisitDate,
    waitingTime: storeWaitingTime,
    setWaitingTime,
    storeId,
  } = useReviewStore()

  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    return resolveInitialVisitFields(storeVisitDate).selectedDate
  })

  const [visitHour, setVisitHour] = useState(() => {
    return resolveInitialVisitFields(storeVisitDate).hour
  })

  const [visitMinute, setVisitMinute] = useState(() => {
    return resolveInitialVisitFields(storeVisitDate).minute
  })

  const [isVisitTimePickerOpen, setIsVisitTimePickerOpen] = useState(false)
  const [pickerHour, setPickerHour] = useState('00')
  const [pickerMinute, setPickerMinute] = useState('00')

  // store의 visitDate가 바뀌면 날짜·시·분 폼 동기화
  // (store가 비어 있으면 초기값과 같은 규칙으로 지금 시각을 채운다 — 안 그러면
  //  마운트 직후 이 이펙트가 기본값을 빈 값으로 되돌린다)
  useEffect(() => {
    const {
      selectedDate: nextDate,
      hour,
      minute,
    } = resolveInitialVisitFields(storeVisitDate)
    setSelectedDate(nextDate)
    setVisitHour(hour)
    setVisitMinute(minute)
  }, [storeVisitDate])

  // 오늘 날짜 (최대 선택 가능 날짜)
  const maxDate = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today
  }, [])

  const waitingTimeOptions: SelectOption[] = useMemo(
    () => buildWaitingTimeOptions(),
    []
  )

  const [waitingTime, setWaitingTimeLocal] = useState<SelectOption | null>(
    () => {
      // store에 남아 있는 값이 있으면 그대로, 없으면 '없음'.
      // 예전 단위로 저장된 값(210분 등)은 toWaitingTimeOption이 '2시간 이상'으로 묶는다.
      if (storeWaitingTime) {
        const minutes = parseInt(storeWaitingTime.value, 10)
        if (!Number.isNaN(minutes)) {
          return toWaitingTimeOption(minutes) ?? storeWaitingTime
        }
        return storeWaitingTime
      }
      return { value: WAITING_TIME_NONE, label: '없음' }
    }
  )


  const [closedDates, setClosedDates] = useState<string[]>([])
  const [isLoadingClosedDates, setIsLoadingClosedDates] =
    useState<boolean>(false)
  const [dateError, setDateError] = useState<string>('')
  const [timeError, setTimeError] = useState<string>('')
  const [isWaitingTimeSelectOpen, setIsWaitingTimeSelectOpen] = useState(false)
  const contentScrollRef = useRef<HTMLDivElement | null>(null)
  const visitHourOptions = useMemo(() => {
    return Array.from({ length: 24 }, (_, hour) => {
      return String(hour).padStart(2, '0')
    })
  }, [])
  const visitMinuteOptions = useMemo(() => {
    return Array.from({ length: 60 / VISIT_MINUTE_STEP }, (_, i) => {
      return String(i * VISIT_MINUTE_STEP).padStart(2, '0')
    })
  }, [])

  // 페이지 초기화: 가게의 휴일 리스트를 API에서 가져오기 및 waitingTime 초기화
  useEffect(() => {
    // waitingTime 초기화 (store에서 가져온 값이 있으면 옵션 리스트에서 찾아서 설정)
    if (storeWaitingTime) {
      const foundOption = waitingTimeOptions.find(
        (opt) => opt.value === storeWaitingTime.value
      )
      if (foundOption) {
        setWaitingTimeLocal(foundOption)
        setWaitingTime(foundOption) // store에도 저장
      } else {
        // 옵션 리스트에 없으면 store 값 그대로 사용
        setWaitingTimeLocal(storeWaitingTime)
        setWaitingTime(storeWaitingTime) // store에도 저장
      }
    } else {
      // store에 값이 없으면 기본값으로 설정
      const defaultOption = { value: 'none', label: '없음' }
      setWaitingTimeLocal(defaultOption)
      setWaitingTime(defaultOption) // store에도 저장
    }

    // 가게의 휴일 리스트를 API에서 가져오기
    const loadClosedDates = async () => {
      if (!storeId) {
        return
      }

      setIsLoadingClosedDates(true)
      try {
        const placeData = await mapApi.getPlace(storeId)
        const businessHours = placeData.business_hours

        // 정기 휴무일인 요일 찾기
        const closedDays: number[] = []
        const dayMap: Record<string, number> = {
          월요일: 0,
          화요일: 1,
          수요일: 2,
          목요일: 3,
          금요일: 4,
          토요일: 5,
          일요일: 6,
        }

        businessHours.forEach((hour) => {
          if (hour.isRegularHoliday) {
            const dayOfWeek = dayMap[hour.day]
            if (dayOfWeek !== undefined) {
              closedDays.push(dayOfWeek)
            }
          }
        })

        // 현재 달과 다음 달의 해당 요일 날짜들 계산
        const now = new Date()
        const closedDateStrings: string[] = []

        // 현재 달과 다음 달 확인
        for (let monthOffset = 0; monthOffset < 2; monthOffset++) {
          const checkDate = new Date(
            now.getFullYear(),
            now.getMonth() + monthOffset,
            1
          )
          const lastDay = new Date(
            checkDate.getFullYear(),
            checkDate.getMonth() + 1,
            0
          ).getDate()

          for (let day = 1; day <= lastDay; day++) {
            const date = new Date(
              checkDate.getFullYear(),
              checkDate.getMonth(),
              day
            )
            const dayOfWeek = (date.getDay() + 6) % 7 // 일요일=0을 일요일=6으로 변환 (월요일=0)

            if (closedDays.includes(dayOfWeek)) {
              const dateString = date.toISOString().split('T')[0]
              // 오늘 이전 날짜는 제외
              if (date <= maxDate) {
                closedDateStrings.push(dateString)
              }
            }
          }
        }

        setClosedDates(closedDateStrings)
      } catch (error) {
        console.error('가게 휴일 정보 로드 실패:', error)
      } finally {
        setIsLoadingClosedDates(false)
      }
    }

    if (storeId) {
      loadClosedDates()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // 빈 의존성 배열: 페이지 초기 로드 시 한 번만 실행

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date)
    setDateError('')
  }

  const applyVisitTimeFieldValidation = (hour: string, minute: string) => {
    if (!hour && !minute) {
      setTimeError('')

      return
    }

    if (minute.length < VISIT_TIME_PART_MAX_LEN || hour.length < 1) {
      setTimeError('')

      return
    }

    const composed = `${hour}:${minute}`

    if (VISIT_TIME_INPUT_REGEX.test(composed)) {
      setTimeError('')
    } else {
      setTimeError(VISIT_TIME_FORMAT_MESSAGE)
    }
  }

  const handleOpenVisitTimePicker = () => {
    setPickerHour(visitHour.padStart(2, '0') || '00')
    setPickerMinute(visitMinute ? snapMinuteToStep(visitMinute) : '00')
    setIsVisitTimePickerOpen(true)
  }

  const handleCloseVisitTimePicker = () => {
    setIsVisitTimePickerOpen(false)
  }

  const {
    handleProps: timePickerHandleProps,
    sheetStyle: timePickerSheetStyle,
  } = useSheetDragDismiss({
    onClose: handleCloseVisitTimePicker,
    enabled: isVisitTimePickerOpen,
  })

  const handleResetVisitTime = () => {
    setVisitHour('')
    setVisitMinute('')
    setTimeError('')
    setIsVisitTimePickerOpen(false)
  }

  const handleConfirmVisitTime = () => {
    setVisitHour(pickerHour)
    setVisitMinute(pickerMinute)
    applyVisitTimeFieldValidation(pickerHour, pickerMinute)
    setIsVisitTimePickerOpen(false)
  }

  const handleVisitHourOptionPick = (hour: string) => {
    setPickerHour(hour)
  }

  const handleVisitMinuteOptionPick = (minute: string) => {
    setPickerMinute(minute)
  }

  const getVisitTimeDisplayLabel = () => {
    if (
      visitHour.length > 0 &&
      visitMinute.length === VISIT_TIME_PART_MAX_LEN
    ) {
      return `${visitHour.padStart(2, '0')}:${visitMinute}`
    }

    return '선택해주세요'
  }

  const handleSubmit = () => {
    const composedTime =
      visitHour.trim().length > 0 &&
      visitMinute.trim().length === VISIT_TIME_PART_MAX_LEN
        ? `${visitHour.trim()}:${visitMinute.trim()}`
        : ''

    const hasTimePartial =
      visitHour.trim().length > 0 || visitMinute.trim().length > 0
    const hasTimeComplete =
      composedTime !== '' && VISIT_TIME_INPUT_REGEX.test(composedTime)

    const hasDate = selectedDate !== null
    const hasWaitingInput = waitingTime !== null && waitingTime.value !== 'none'

    let firstErrorMessage = ''

    setDateError('')

    if (hasTimePartial && !hasTimeComplete) {
      const errorMessage = VISIT_TIME_FORMAT_MESSAGE
      setTimeError(errorMessage)
      firstErrorMessage = errorMessage
    } else {
      setTimeError('')
    }

    if (firstErrorMessage) {
      toastUtil.warning(firstErrorMessage)

      return
    }

    if (!hasDate && !hasTimeComplete && !hasWaitingInput) {
      const errorMessage =
        '날짜·방문 시간·웨이팅 중 최소 한 가지를 입력해주세요'
      toastUtil.warning(errorMessage)

      return
    }

    let visitDateString: string | null = null

    if (hasDate && hasTimeComplete && selectedDate) {
      const year = selectedDate.getFullYear()
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0')
      const day = String(selectedDate.getDate()).padStart(2, '0')
      const dateString = `${year}-${month}-${day}`

      visitDateString = `${dateString} ${composedTime}`
    } else if (hasDate && selectedDate) {
      const year = selectedDate.getFullYear()
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0')
      const day = String(selectedDate.getDate()).padStart(2, '0')
      visitDateString = `${year}-${month}-${day}`
    } else if (hasTimeComplete) {
      visitDateString = composedTime
    }

    setVisitDate(visitDateString)
    setWaitingTime(waitingTime)
    navigate(-1)
  }

  const handleBack = () => {
    // 뒤로가기 (저장하지 않음)
    navigate(-1)
  }

  const composedVisitTime =
    visitHour.length > 0 && visitMinute.length === VISIT_TIME_PART_MAX_LEN
      ? `${visitHour}:${visitMinute}`
      : ''
  const isTimeValid = composedVisitTime
    ? VISIT_TIME_INPUT_REGEX.test(composedVisitTime)
    : false
  const hasWaitingInput = waitingTime !== null && waitingTime.value !== 'none'
  const isFormValid = selectedDate !== null || isTimeValid || hasWaitingInput

  const renderCalendar = () => {
    return (
      <div className="mb-24px flex flex-col">
        <CustomCalendar
          selectedDate={selectedDate}
          onDateSelect={handleDateSelect}
          closedDates={closedDates}
          closedDateMessage="해당 날짜는 가게의 휴일입니다"
          futureDateMessage="미래 날짜는 선택할 수 없습니다"
          maxDate={maxDate}
        />

        <FormErrorMessage error={dateError} />
      </div>
    )
  }

  const renderVisitTime = () => {
    const displayLabel = getVisitTimeDisplayLabel()

    return (
      <div className="mt-16px w-full flex flex-col">
        <label
          className="font-pretendard font-700 text-16px leading-125% text-brown-80%-main"
          htmlFor="visit-time-picker-trigger"
        >
          방문 시간
        </label>

        <div className="mt-10px w-full">
          <button
            type="button"
            id="visit-time-picker-trigger"
            onClick={handleOpenVisitTimePicker}
            className={`
              w-full min-h-44px py-10px px-12px flex items-center justify-between gap-8px
              rounded-8px border border-[rgba(71,36,16,0.2)] bg-white
              shadow-[0px_4px_10px_0px_rgba(117,76,0,0.1)]
              font-pretendard font-regular text-16px text-left text-[rgba(75,54,33,0.8)]
            `}
          >
            <span className="min-w-0 flex-1 truncate">{displayLabel}</span>
            <img
              src="/images/chevron-right-icon.png"
              alt=""
              className="shrink-0 w-20px h-20px object-contain rotate-90"
            />
          </button>
        </div>

        <FormErrorMessage error={timeError} />
      </div>
    )
  }

  const handleWaitingTimeChange = (selected: SelectOption | null) => {
    setWaitingTimeLocal(selected)
    setWaitingTime(selected) // reviewStore에 저장
  }

  const handleWaitingTimeOptionPick = (option: SelectOption) => {
    handleWaitingTimeChange(option)
    setIsWaitingTimeSelectOpen(false)
  }

  const handleWaitingTimeToggle = () => {
    setIsWaitingTimeSelectOpen((open) => {
      const nextOpen = !open

      if (nextOpen) {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            const scrollContainer = contentScrollRef.current

            if (!scrollContainer) {
              return
            }

            scrollContainer.scrollTo({
              top: scrollContainer.scrollHeight,
              behavior: 'smooth',
            })
          })
        })
      }

      return nextOpen
    })
  }

  const renderWaitingTime = () => {
    const displayLabel = waitingTime?.label ?? '선택해주세요'

    return (
      <div className="mt-16px w-full flex flex-col">
        <label
          className="font-pretendard font-700 text-16px leading-125% text-brown-80%-main"
          htmlFor="waiting-time-select-trigger"
        >
          웨이팅 시간
        </label>

        <div className="mt-10px w-full flex flex-col">
          <div
            className={`
              w-full rounded-8px border bg-white overflow-hidden
              shadow-[0px_4px_10px_0px_rgba(117,76,0,0.1)]
              ${
                isWaitingTimeSelectOpen
                  ? 'border-brandPrimary'
                  : 'border-[rgba(71,36,16,0.2)]'
              }
            `}
          >
            <button
              type="button"
              id="waiting-time-select-trigger"
              onClick={handleWaitingTimeToggle}
              aria-expanded={isWaitingTimeSelectOpen}
              aria-haspopup="listbox"
              className={`
                w-full min-h-44px py-10px px-12px flex items-center justify-between gap-8px
                font-pretendard font-regular text-16px text-left cursor-pointer
                text-[rgba(75,54,33,0.8)]
              `}
            >
              <span className="min-w-0 flex-1 truncate">{displayLabel}</span>
              <img
                src="/images/chevron-right-icon.png"
                alt=""
                className={`shrink-0 w-20px h-20px object-contain transition-transform ${
                  isWaitingTimeSelectOpen ? '-rotate-90' : 'rotate-90'
                }`}
              />
            </button>

            {isWaitingTimeSelectOpen ? (
              <div
                className="border-t border-[rgba(71,36,16,0.12)] max-h-[min(360px,55vh)] overflow-y-auto scrollbar-hide"
                role="listbox"
              >
                {waitingTimeOptions.map((option) => {
                  const isSelected = waitingTime?.value === option.value

                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => handleWaitingTimeOptionPick(option)}
                      className={`
                        w-full py-10px px-12px text-left font-pretendard font-regular text-16px
                        text-[rgba(75,54,33,0.8)] cursor-pointer
                        ${
                          isSelected
                            ? 'bg-[#F8F4EE]'
                            : 'bg-white active:bg-[#FAF6EF]'
                        }
                      `}
                    >
                      {option.label}
                    </button>
                  )
                })}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    )
  }

  const renderVisitTimePickerModal = () => {
    if (!isVisitTimePickerOpen) {
      return null
    }

    return (
      <>
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-[1050]"
          onClick={handleCloseVisitTimePicker}
        />

        <div className="fixed inset-0 z-[1050] flex items-end justify-center pointer-events-none">
          <div
            className={`
              app-container bg-ivoryKey rounded-t-30px
              flex flex-col overflow-hidden pointer-events-auto
            `}
            style={timePickerSheetStyle}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 드래그 핸들 (아래로 끌면 닫힘) */}
            <div
              className="w-full flex justify-center pt-16px pb-24px"
              {...timePickerHandleProps}
            >
              <div className="w-40px h-4px bg-472410-20% rounded-full" />
            </div>

            {/* 헤더 (공용) */}
            <SheetHeader title="방문 시간 선택" />

            <div className="px-20px py-20px flex items-start gap-12px">
              <div className="flex-1 min-w-0">
                <div className="h-220px rounded-12px border border-[rgba(71,36,16,0.12)] overflow-y-auto scrollbar-hide bg-white">
                  {visitHourOptions.map((hour) => {
                    const isSelected = pickerHour === hour

                    return (
                      <button
                        key={hour}
                        type="button"
                        onClick={() => handleVisitHourOptionPick(hour)}
                        className={`
                          w-full py-10px px-12px text-center font-pretendard text-16px
                          ${
                            isSelected
                              ? 'bg-[#F8F4EE] text-brown-80%-main font-700'
                              : 'text-[rgba(75,54,33,0.8)] font-regular'
                          }
                        `}
                      >
                        {hour}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="h-220px rounded-12px border border-[rgba(71,36,16,0.12)] overflow-y-auto scrollbar-hide bg-white">
                  {visitMinuteOptions.map((minute) => {
                    const isSelected = pickerMinute === minute

                    return (
                      <button
                        key={minute}
                        type="button"
                        onClick={() => handleVisitMinuteOptionPick(minute)}
                        className={`
                          w-full py-10px px-12px text-center font-pretendard text-16px
                          ${
                            isSelected
                              ? 'bg-[#F8F4EE] text-brown-80%-main font-700'
                              : 'text-[rgba(75,54,33,0.8)] font-regular'
                          }
                        `}
                      >
                        {minute}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>

            <div className="px-20px pb-32px flex items-center gap-8px">
              <button
                type="button"
                onClick={handleResetVisitTime}
                className={`
                  flex-1 h-44px rounded-12px bg-472410-10%
                  font-pretendard font-700 text-16px text-brown-80%-main
                `}
              >
                시간 지우기
              </button>
              <button
                type="button"
                onClick={handleConfirmVisitTime}
                className={`
                  flex-1 h-44px rounded-12px bg-brown-80%-main
                  font-pretendard font-700 text-16px text-white
                `}
              >
                선택 완료
              </button>
            </div>
          </div>
        </div>
      </>
    )
  }

  const renderSubmitButton = () => {
    return (
      <SubmitButton
        text="완료"
        onSubmit={handleSubmit}
        disabled={!isFormValid}
        onDisabled={handleSubmit}
      />
    )
  }

  const renderAppBar = () => {
    return (
      <div className="app-container sticky top-0 z-10 bg-backgroundColorBg2">
        <AppBar title="방문일자 선택" isBottomBorder onBack={handleBack} />
      </div>
    )
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      {renderAppBar()}

      <div className="app-container flex-1 flex flex-col overflow-hidden">
        {/* 전체 로딩 상태 */}
        {isLoadingClosedDates ? (
          <div className="flex-1 flex items-center justify-center">
            <LoadingSpinner size={28} borderWidth={4} />
          </div>
        ) : (
          <>
            {/* 컨텐츠 영역 (하단 고정 버튼 높이만큼 패딩) */}
            <div
              ref={contentScrollRef}
              className="flex-1 overflow-y-auto scrollbar-hide px-20px py-16px"
              style={{
                paddingBottom: '189px',
              }}
            >
              {/* 캘린더 */}
              {renderCalendar()}

              {/* divider */}
              <Divider className="mt-16px" />

              {/* 방문 시간 입력 */}
              {renderVisitTime()}

              {/* 웨이팅 시간 */}
              {renderWaitingTime()}
            </div>

            {/* 하단 고정 완료 버튼 */}
            <div
              className={`
                fixed bottom-0 left-0 right-0 z-10
                bg-transparent pb-safe
              `}
            >
              <div className="app-container px-20px pb-32px">
                {renderSubmitButton()}
              </div>
            </div>
          </>
        )}
      </div>

      {renderVisitTimePickerModal()}
    </div>
  )
}

export default VisitDateAdd
